<script setup lang="ts">
import { DOMContentLoadedPromise } from '@@/bits/dom'
import { computed, onMounted, ref } from 'vue'

const COLORED_ICONS: Record<string, number> = {
  'amazon-color': 2,
  'paypal-color': 3,
  'discover-color': 3,
  'jcb-color': 5,
  'mastercard-color': 3,
  'unionpay-color': 5,
  'amex-color': 2,
  'dinersclub-color': 3,
  'visa-color': 3,
}

const props = withDefaults(
  defineProps<{
    name: string
    size?: number | 'small' | 'normal' | 'large'
    ariaHidden?: 'true' | 'false'
  }>(),
  {
    size: 'normal',
    ariaHidden: 'true',
  },
)

defineEmits<{
  (name: 'click', event: MouseEvent): void
}>()

const isDomLoaded = ref<boolean>(false)

const isColoredIcon = computed<boolean>(() => {
  return Object.keys(COLORED_ICONS).includes(props.name)
})

const numOfPaths = computed(() => {
  return COLORED_ICONS[props.name]
})

const classes = computed(() => {
  const classList: string[] = []
  if (isDomLoaded.value) {
    classList.push('immaterial-icons')
    if (props.size === 'small') {
      classList.push('immaterial-icons-18')
    }
    if (props.size === 'large') {
      classList.push('immaterial-icons-32')
    }
  } else {
    classList.push('immaterial-placeholder')
    if (props.size === 'small') {
      classList.push('immaterial-placeholder-18')
    }
    if (props.size === 'large') {
      classList.push('immaterial-placeholder-32')
    }
  }
  return classList
})

const styles = computed(() => {
  if (typeof props.size === 'number') {
    if (isDomLoaded.value) {
      return `font-size: ${props.size}px; max-width:${props.size}px; width: ${props.size}px; height: ${props.size}px; overflow: visible;`
    } else {
      return `max-width:${props.size}px; width: ${props.size}px; height: ${props.size}px;`
    }
  }
  return ''
})

onMounted(() => {
  DOMContentLoadedPromise.then(() => {
    if (!isDomLoaded.value) {
      isDomLoaded.value = true
    }
  })
})
</script>

<template>
  <span v-if="isColoredIcon" :class="[classes, `icon-${name}`]" :style="styles">
    <span v-for="num in numOfPaths" :key="num" :class="`path${num}`"></span>
  </span>
  <i v-else :class="classes" :style="styles" :aria-hidden="ariaHidden" @click="$emit('click', $event)">{{ name }}</i>
</template>

<style lang="scss" scoped>
@import '@@/styles/3/partials/fonts/material_icons';

.immaterial-icons {
  @include immaterial-icon;
}

.immaterial-icons-18 {
  font-size: 18px;
}

.immaterial-icons-32 {
  font-size: 32px;
  padding: 5px;
}

.immaterial-placeholder {
  visibility: hidden;
  width: 24px;
  height: 24px;
  display: inline-block;
}

.immaterial-placeholder-18 {
  visibility: hidden;
  width: 18px;
  height: 18px;
  display: inline-block;
}

.immaterial-placeholder-32 {
  visibility: hidden;
  width: 32px;
  height: 32px;
  padding: 5px;
  display: inline-block;
}
</style>
